import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import StatusBadge from "@/components/app/StatusBadge";
import { AlertTriangle, TrendingUp, Package, Truck, CheckCircle2, DollarSign } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import ComplianceSummaryCard from "@/pages/dashboard/ComplianceSummaryCard";
import FieldOpsQuickLaunch from "@/pages/dashboard/FieldOpsQuickLaunch";

const KPI = ({ label, value, sub, icon: Icon, testid, accent }) => (
  <div className="bg-white border border-gray-200 p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="label-caps">{label}</div>
      <Icon className={`w-4 h-4 ${accent || "text-gray-400"}`} />
    </div>
    <div className="stat-value text-4xl mt-3 text-gray-900">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-2 font-mono">{sub}</div>}
  </div>
);

function money(n) {
  return "$" + (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loads, setLoads] = useState([]);

  const canSeeSummary = ["admin", "project_manager", "compliance_officer"].includes(user?.role);

  useEffect(() => {
    (async () => {
      const [s, l] = await Promise.all([api.get("/dashboard/stats"), api.get("/loads")]);
      setStats(s.data);
      setLoads(l.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hazardousLoads = loads.filter((l) => l.hazardous && l.status !== "delivered");
  const recentLoads = loads.slice(0, 6);

  return (
    <div>
      <PageHeader
        title="Operations Dashboard"
        subtitle="Live yard status"
        actions={
          <Link
            to="/loads"
            data-testid="dashboard-create-load-cta"
            className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold uppercase tracking-widest px-4 py-2.5 transition-colors"
          >
            + New Load
          </Link>
        }
      />

      <div className="p-8">
        {canSeeSummary && <ComplianceSummaryCard />}
        <FieldOpsQuickLaunch />
        {/* KPI Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPI label="Active In-Transit" value={stats?.active_in_transit ?? "—"} icon={Truck} accent="text-amber-500" sub="Currently on route" testid="kpi-in-transit" />
          <KPI label="Pending Pickups" value={stats?.pending_pickups ?? "—"} icon={Package} sub="Awaiting dispatch" testid="kpi-pending" />
          <KPI label="Delivered Today" value={stats?.delivered_today ?? "—"} icon={CheckCircle2} accent="text-emerald-500" sub="Since midnight" testid="kpi-delivered" />
          <KPI label="Monthly Expense" value={money(stats?.monthly_revenue)} icon={DollarSign} sub="Invoiced this month" testid="kpi-revenue" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <div className="bg-white border border-gray-200 p-5" data-testid="chart-weekly-loads">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-caps">Weekly Loads</div>
                <div className="font-display font-bold text-xl mt-1 text-gray-900">Last 7 days</div>
              </div>
              <TrendingUp className="w-4 h-4 text-orange-500" />
            </div>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={stats?.weekly_loads || []}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="day" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "#111827", border: "none", borderRadius: 2, color: "#fff", fontSize: 12 }} />
                  <Bar dataKey="loads" fill="#F97316" radius={[0, 0, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-gray-200 p-5" data-testid="chart-monthly-revenue">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-caps">Expense Trend</div>
                <div className="font-display font-bold text-xl mt-1 text-gray-900">Last 6 months</div>
              </div>
              <DollarSign className="w-4 h-4 text-gray-500" />
            </div>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={stats?.monthly_revenue_chart || []}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="month" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "#111827", border: "none", borderRadius: 2, color: "#fff", fontSize: 12 }} formatter={(v) => money(v)} />
                  <Line type="monotone" dataKey="revenue" stroke="#111827" strokeWidth={2} dot={{ fill: "#F97316", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Bottom row: Hazardous + Recent Loads */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 p-5 lg:col-span-1" data-testid="hazardous-panel">
            <div className="flex items-center justify-between mb-4">
              <div className="label-caps text-red-600">Hazardous Active</div>
              <AlertTriangle className="w-4 h-4 text-red-500" />
            </div>
            {hazardousLoads.length === 0 ? (
              <div className="text-sm text-gray-500 font-mono">No hazardous loads active.</div>
            ) : (
              <div className="space-y-3">
                {hazardousLoads.slice(0, 5).map((l) => (
                  <div key={l.id} className="border-l-2 border-red-500 pl-3">
                    <div className="font-mono text-xs text-gray-900">{l.bol_number}</div>
                    <div className="text-sm text-gray-700">{l.soil_type} · {l.weight_tons} tons</div>
                    <StatusBadge status={l.status} className="mt-1" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 lg:col-span-2 overflow-hidden" data-testid="recent-loads-panel">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <div className="label-caps">Recent Loads</div>
              <Link to="/loads" className="text-xs font-semibold text-orange-600 hover:text-orange-700 uppercase tracking-widest">View all →</Link>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-5 py-2 label-caps">BOL</th>
                  <th className="text-left px-5 py-2 label-caps">Material</th>
                  <th className="text-right px-5 py-2 label-caps">Tons</th>
                  <th className="text-left px-5 py-2 label-caps">Status</th>
                </tr>
              </thead>
              <tbody>
                {recentLoads.length === 0 ? (
                  <tr><td colSpan="4" className="px-5 py-8 text-center text-sm text-gray-500">No loads yet. Create your first load.</td></tr>
                ) : recentLoads.map((l) => (
                  <tr key={l.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-5 py-3 font-mono text-xs text-gray-900">{l.bol_number}</td>
                    <td className="px-5 py-3 text-sm text-gray-800">{l.soil_type}</td>
                    <td className="px-5 py-3 text-right font-mono text-sm">{l.weight_tons}</td>
                    <td className="px-5 py-3"><StatusBadge status={l.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
